import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

/**
 * Reusable Filter Bar component for consistent filtering across the application
 * 
 * @param {Object} props - Component props
 * @param {string} props.searchValue - Current search term value
 * @param {function} props.onSearchChange - Handler for search input changes
 * @param {Array} props.filters - Array of filter objects (select inputs)
 * @param {Object} props.primaryButton - Configuration for primary action button
 * @param {Object} props.secondaryButton - Configuration for secondary action button (optional)
 * @param {function} props.onReset - Handler for resetting filters (optional)
 */
const FilterBar = ({
  searchValue = '',
  onSearchChange,
  filters = [],
  primaryButton,
  secondaryButton,
  onReset,
}) => {
  return (
    <Card
      sx={{
        mb: 3,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: 3,
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}
    >
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          {/* Search Field */}
          <Grid item xs={12} md={4}>
                          <TextField
              fullWidth
              placeholder={searchValue.placeholder || "Rechercher..."}
              value={searchValue.value}
              onChange={onSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '& .MuiInputBase-input': {
                  color: 'white'
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  opacity: 1
                }
              }}
            />
          </Grid>

          {/* Filter Select Inputs */}
          {filters.map((filter, index) => (
            <Grid item xs={12} md={filter.width || (filters.length <= 2 ? 3 : 2)} key={index}>
              <FormControl fullWidth>
                {filter.label && <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{filter.label}</InputLabel>}
                <Select
                  value={filter.value}
                  onChange={filter.onChange}
                  label={filter.label}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.5)'
                    },
                    '& .MuiInputBase-input': {
                      color: 'white'
                    }
                  }}
                >
                  {filter.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}

          {/* Action Buttons */}
          <Grid item xs={12} md={secondaryButton ? 2 : (filters.length <= 1 ? 3 : 2)}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {secondaryButton && (
                <Button
                  variant="outlined"
                  startIcon={secondaryButton.icon || <FilterAltIcon />}
                  onClick={secondaryButton.onClick}
                  sx={{ 
                    borderRadius: 2, 
                    flex: 1,
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  {secondaryButton.label}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={primaryButton.icon}
                onClick={primaryButton.onClick}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                  flex: 1,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
                  }
                }}
              >
                {primaryButton.label}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default FilterBar;
